import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  PolicySchema,
  PolicyCreateInputSchema,
  PolicyUpdateInputSchema,
  type Policy,
  type PolicyCreateInput,
  type PolicyUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `policy` (barèmes et conditions org-wide, versionnés).
 *
 * Réplique le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`PolicyCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide.
 */

export type { PolicyCreateInput };

/** Nom de la collection Firestore de la famille policy. */
export const POLICIES_COLLECTION = 'policies';

/**
 * Repository typé pour la famille `policy`.
 * Toute lecture est validée par `PolicySchema`, toute écriture par
 * `PolicyCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface PolicyRepository {
  create(input: PolicyCreateInput): Promise<Policy>;
  update(id: string, patch: PolicyUpdateInput): Promise<Policy>;
  list(options?: ListOptions): Promise<Policy[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Policy | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Policy>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Policy>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Policy>;
}

const policyConverter = createConverter<Policy>((raw: DocumentData) => PolicySchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createPolicyRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): PolicyRepository {
  const col = collection(db, POLICIES_COLLECTION).withConverter(policyConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Policy>(db, col, POLICIES_COLLECTION, getActorId);

  return {
    async create(input: PolicyCreateInput): Promise<Policy> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = PolicyCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.domain !== undefined) optional['domain'] = fields.domain;
      if (fields.rules !== undefined) optional['rules'] = fields.rules;
      if (fields.version !== undefined) optional['version'] = fields.version;
      if (fields.effectiveFrom !== undefined) optional['effectiveFrom'] = fields.effectiveFrom;
      if (fields.appliesToRefs !== undefined) optional['appliesToRefs'] = fields.appliesToRefs;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Policy);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return PolicySchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: PolicyUpdateInput): Promise<Policy> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = PolicyUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, POLICIES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec PolicySchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`policy ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Policy[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Policy | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
